import { useEffect, useState } from 'react'
import {
  notification,
  TimePicker,
  Select as AntSelect,
  Form,
  DatePicker,
} from 'antd'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { AlertCircle } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Drawer, DrawerFooter, DrawerSection } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import {
  createRobotEvent,
  fetchRobotEventDetail,
  updateRobotEvent,
} from '@/api/robot-events.api'
import { fetchEvents } from '@/api/events.api'
import type { RobotEventRow } from './columnsEvent'
import { Select } from '@/components/ui/select'

interface RobotEventDrawerProps {
  open: boolean
  onClose: () => void
  robotId: string
  mode?: 'create' | 'update' | 'view'
  eventData?: RobotEventRow | null
  onSaved?: () => Promise<void> | void
}

const EVENT_TYPE_OPTIONS = [
  { value: 1, label: 'Khẩn cấp' },
  { value: 2, label: 'Lập lịch' },
]

const STATUS_OPTIONS = [
  { value: 1, label: 'Hoạt động' },
  { value: 0, label: 'Không hoạt động' },
]

type DrawerMode = 'create' | 'update' | 'view'

type RobotEventFormValues = {
  eventId: string
  eventName: string
  eventType: number
  startDate?: Dayjs
  endDate?: Dayjs
  startTime?: Dayjs
  endTime?: Dayjs
  active: number
}

const toDrawerTitle = (mode: DrawerMode, isUpdateMode: boolean) => {
  if (mode === 'view') return 'CHI TIẾT SỰ KIỆN'
  if (isUpdateMode) return 'CẬP NHẬT SỰ KIỆN'
  return 'THÊM MỚI SỰ KIỆN'
}

const isToday = (date?: Dayjs) => !!date && date.isSame(dayjs(), 'day')

const toEventPayload = (values: RobotEventFormValues) => ({
  eventId: values.eventId,
  eventName: values.eventName,
  eventType: Number(values.eventType),
  startDate: values.startDate
    ? values.startDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    : '',
  endDate: values.endDate
    ? values.endDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    : '',
  active: values.active,
  startTime: values.startTime ? values.startTime.format('HH:mm') : '',
  endTime: values.endTime ? values.endTime.format('HH:mm') : '',
})

export function RobotEventDrawer({
  open,
  onClose,
  robotId,
  mode = 'create',
  eventData,
  onSaved,
}: RobotEventDrawerProps) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm<RobotEventFormValues>()

  const [currentMode, setCurrentMode] = useState<DrawerMode>(mode)

  useEffect(() => {
    if (open) {
      setCurrentMode(mode)
    }
  }, [open, mode])

  const isViewMode = currentMode === 'view'
  const isUpdateMode = currentMode === 'update'

  const eventType = Form.useWatch('eventType', form)

  const { data: eventDetail } = useQuery({
    queryKey: ['robot-event-detail', robotId, eventData?.id],
    queryFn: ({ signal }) =>
      eventData?.id
        ? fetchRobotEventDetail(robotId, eventData.id, signal)
        : Promise.resolve(null),
    enabled: open && mode !== 'create' && !!eventData?.id,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

  useEffect(() => {
    if (!open) {
      form.resetFields()
      return
    }
    if (mode !== 'create' && eventDetail) {
      form.setFieldsValue({
        eventId: eventDetail.eventId,
        eventType: Number(eventDetail.eventTypeValue || 2),
        startDate: eventDetail.startDate
          ? dayjs(eventDetail.startDate)
          : undefined,
        endDate: eventDetail.endDate ? dayjs(eventDetail.endDate) : undefined,
        startTime: eventDetail.startTime
          ? dayjs(eventDetail.startTime, 'HH:mm:ss')
          : undefined,
        endTime: eventDetail.endTime
          ? dayjs(eventDetail.endTime, 'HH:mm:ss')
          : undefined,
        active: Number(eventDetail.active),
      })
    }
  }, [open, mode, eventDetail, form])

  const saveMutation = useMutation({
    mutationFn: async (values: RobotEventFormValues) => {
      const payload = toEventPayload(values)
      return isUpdateMode && eventData?.id
        ? updateRobotEvent(robotId, eventData.id, payload)
        : createRobotEvent(robotId, payload)
    },
    onSuccess: async () => {
      notification.success({
        message: isUpdateMode ? 'Cập nhật thành công!' : 'Thêm mới thành công!',
      })
      if (eventData?.id) {
        await queryClient.invalidateQueries({
          queryKey: ['robot-event-detail', robotId, eventData.id],
        })
      }
      await onSaved?.()
      onClose()
    },
  })

  const handleFinish = (values: RobotEventFormValues) => {
    saveMutation.mutate(values)
  }

  const handleOpenUpdateMode = () => setCurrentMode('update')

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={toDrawerTitle(currentMode, isUpdateMode)}
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={false}
            className="min-w-[160px] h-10"
          >
            {isViewMode ? 'Đóng' : 'Hủy'}
          </Button>

          {isViewMode ? (
            <Button
              variant="primary"
              size="md"
              disabled={false}
              onClick={handleOpenUpdateMode}
              className="min-w-[160px] h-10"
            >
              Cập nhật
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={() => form.submit()}
              loading={saveMutation.isPending}
              className="min-w-[160px] h-10"
            >
              Lưu
            </Button>
          )}
        </DrawerFooter>
      }
    >
      <Form
        form={form}
        className="robot-event-form"
        initialValues={{
          active: 1,
          eventType: 1,
        }}
        requiredMark={(label, info) => (
          <>
            {label}
            {info.required && <span className="text-destructive ml-1">*</span>}
          </>
        )}
        layout="vertical"
        onFinish={handleFinish}
        disabled={isViewMode}
      >
        {eventType === 1 && (
          <div className="flex items-start gap-3 rounded-2xl border border-[#F28B82] bg-[#FFF1F1] px-4 py-3 mb-3">
            <AlertCircle size={18} className="mt-1.5 shrink-0 text-[#E24B4B]" />
            <div className="space-y-1">
              <div className="text-lg font-semibold text-[#374151]">Lưu ý</div>
              <p className="text-sm text-[#6B7280]">
                Khi kích hoạt sự kiện khẩn cấp, sự kiện đang ở trạng thái "Hoạt
                động" sẽ tự động được chuyển sang "Không hoạt động".
              </p>
            </div>
          </div>
        )}

        <DrawerSection title="Thông tin chung">
          <div className="space-y-3 pt-2 pb-5">
            <Form.Item
              name="eventId"
              label="Sự kiện"
              rules={[{ required: true, message: 'Vui lòng chọn sự kiện' }]}
            >
              <Select
                lazy
                cacheKey="robot-event-select"
                initialPage={0}
                placeholder="Chọn sự kiện"
                allowClear
                enableRemoteSearch
                className="w-full bg-white h-10 rounded-full"
                disabled={isViewMode}
                initialOptions={
                  eventDetail?.eventId && eventDetail.eventName
                    ? [
                        {
                          value: eventDetail.eventId,
                          label: eventDetail.eventName,
                        },
                      ]
                    : []
                }
                loadOptions={async ({ page, signal, searchText }) => {
                  const res = await fetchEvents(
                    page,
                    10,
                    { keySearch: searchText?.trim() || undefined, status: 1 },
                    signal,
                  )
                  return {
                    options: res.items.map((i) => ({
                      value: i.id,
                      label: i.name,
                    })),
                    hasNextPage: res.items.length === 10,
                    nextPage: page + 1,
                  }
                }}
                onLazyValueChange={(val) => {
                  form.setFieldsValue({ eventId: val })
                }}
              />
            </Form.Item>

            <Form.Item
              name="eventType"
              label="Loại sự kiện"
              rules={[
                { required: true, message: 'Vui lòng chọn loại sự kiện' },
              ]}
            >
              <AntSelect
                placeholder="Chọn loại sự kiện"
                value={eventType}
                className="w-full bg-white h-10 rounded-full"
                options={EVENT_TYPE_OPTIONS}
              />
            </Form.Item>

            <div className="grid grid-cols-1">
              <Form.Item
                name="startDate"
                label="Ngày bắt đầu"
                className="!m-0"
                getValueFromEvent={(date: dayjs.Dayjs | null) => {
                  if (!date) return null
                  return date.startOf('day')
                }}
                rules={[
                  { required: true, message: 'Vui lòng chọn ngày bắt đầu' },
                  () => ({
                    validator(_, value?: Dayjs) {
                      if (!value || currentMode !== 'create')
                        return Promise.resolve()
                      if (value.isBefore(dayjs().startOf('day'))) {
                        return Promise.reject(
                          new Error('Ngày bắt đầu phải từ hôm nay trở đi'),
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <DatePicker
                  className="w-full !bg-white h-10 rounded-full"
                  placeholder="DD/MM/YYYY"
                  format="DD/MM/YYYY"
                  inputReadOnly={isViewMode}
                  allowClear={!isViewMode}
                />
              </Form.Item>

              <Form.Item
                name="endDate"
                label="Ngày kết thúc"
                className="!mt-3"
                dependencies={['startDate']}
                getValueFromEvent={(date: dayjs.Dayjs | null) => {
                  if (!date) return null
                  return date.endOf('day').millisecond(0)
                }}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value?: Dayjs) {
                      if (!value) return Promise.resolve()
                      if (value.isBefore(dayjs().startOf('day'))) {
                        return Promise.reject(
                          new Error('Ngày kết thúc phải từ hôm nay trở đi'),
                        )
                      }

                      const startDate = getFieldValue('startDate') as
                        | Dayjs
                        | undefined
                      if (startDate && value.isBefore(startDate, 'day')) {
                        return Promise.reject(
                          new Error(
                            'Ngày kết thúc phải sau hoặc bằng ngày bắt đầu',
                          ),
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <DatePicker
                  className="w-full !bg-white h-10 rounded-full"
                  placeholder={isViewMode ? '' : 'DD/MM/YYYY'}
                  format="DD/MM/YYYY"
                  inputReadOnly={isViewMode}
                  allowClear={!isViewMode}
                />
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-3 !m-0">
              <Form.Item
                name="startTime"
                label="Thời gian phát"
                dependencies={['startDate', 'endDate', 'endTime']}
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng chọn thời gian bắt đầu',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value?: Dayjs) {
                      const startDate = getFieldValue('startDate') as
                        | Dayjs
                        | undefined
                      const endDate = getFieldValue('endDate') as
                        | Dayjs
                        | undefined
                      const now = dayjs()

                      if (
                        isToday(startDate) &&
                        isToday(endDate) &&
                        value &&
                        value.isBefore(now, 'minute')
                      ) {
                        return Promise.reject(
                          new Error(
                            'Giờ bắt đầu phải lớn hơn hoặc bằng giờ hiện tại',
                          ),
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <TimePicker
                  className="w-full !bg-white h-10 rounded-full"
                  placeholder={isViewMode ? '' : 'HH:mm'}
                  format="HH:mm"
                  inputReadOnly={isViewMode}
                  allowClear={!isViewMode}
                />
              </Form.Item>

              <Form.Item
                name="endTime"
                label={null}
                dependencies={['startDate', 'endDate', 'startTime']}
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng chọn thời gian kết thúc',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value?: Dayjs) {
                      const startDate = getFieldValue('startDate') as
                        | Dayjs
                        | undefined
                      const endDate = getFieldValue('endDate') as
                        | Dayjs
                        | undefined
                      const startTime = getFieldValue('startTime') as
                        | Dayjs
                        | undefined
                      const now = dayjs()

                      if (startTime && value) {
                        if (!value.isAfter(startTime)) {
                          return Promise.reject(
                            new Error('Giờ kết thúc phải lớn hơn giờ bắt đầu'),
                          )
                        }
                      }

                      if (
                        isToday(startDate) &&
                        isToday(endDate) &&
                        value &&
                        value.isBefore(now, 'minute')
                      ) {
                        return Promise.reject(
                          new Error('Giờ kết thúc phải lớn hơn giờ hiện tại'),
                        )
                      }

                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <TimePicker
                  className="w-full !bg-white h-10 rounded-full mt-7"
                  placeholder={isViewMode ? '' : 'HH:mm'}
                  format="HH:mm"
                  inputReadOnly={isViewMode}
                  allowClear={!isViewMode}
                />
              </Form.Item>
            </div>

            <Form.Item
              name="active"
              label="Trạng thái hoạt động"
              className="!m-0"
              required
            >
              <AntSelect
                disabled={isViewMode}
                className="bg-white h-10 rounded-full"
                options={STATUS_OPTIONS}
              />
            </Form.Item>
          </div>
        </DrawerSection>
      </Form>
    </Drawer>
  )
}
